/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : states
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical empty / error / loading states. One look for the
 *                  three states every data view needs, so panels stop
 *                  hand-rolling their own. Token-driven (light + dark).
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { clsx } from 'clsx';
import { Inbox, AlertTriangle } from 'lucide-react';

export interface EmptyStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/** Canonical "nothing here yet" state. */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'Nothing to show',
  message,
  icon,
  action,
  className,
}) => (
  <div className={clsx('flex flex-col items-center justify-center py-12 text-center', className)}>
    <div className="mb-3 text-[var(--color-text-secondary)] opacity-60">
      {icon ?? <Inbox className="h-10 w-10" />}
    </div>
    <p className="text-base font-medium text-[var(--color-text-primary)]">{title}</p>
    {message != null && <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{message}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export interface ErrorStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  onRetry?: () => void;
  className?: string;
}

/** Canonical load-failure state with an optional retry. */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  className,
}) => (
  <div
    className={clsx(
      'rounded-lg border border-[var(--color-status-danger)]/40 bg-[var(--color-status-danger)]/5 p-4',
      className,
    )}
  >
    <div className="flex items-center gap-2 text-[var(--color-status-danger)]">
      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
      <span className="font-medium">{title}</span>
    </div>
    {message != null && <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{message}</p>}
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-3 text-sm font-medium text-[var(--color-accent)] hover:underline"
      >
        Try again
      </button>
    )}
  </div>
);

export interface LoadingStateProps {
  /** Number of skeleton rows. */
  rows?: number;
  className?: string;
}

/** Canonical loading skeleton (use in place of bespoke animate-pulse blocks). */
export const LoadingState: React.FC<LoadingStateProps> = ({ rows = 4, className }) => (
  <div className={clsx('animate-pulse space-y-3', className)} aria-busy="true" aria-live="polite">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-10 rounded bg-[var(--color-bg-subtle)]" />
    ))}
  </div>
);
